import { 
  Box, 
  Grid, 
  Typography, 
  Card, 
  CardContent, 
  Chip, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Button,
  IconButton,
  Tooltip,
  Alert,
  Divider
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query, getDocs, where } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import { isCapitalRoutingRow } from "../../../../Utils/fundsPeriodTotals";
import ShowYearlyGraph from "./ShowYearlyGraph";
import Skeleton from "react-loading-skeleton";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Analytics as AnalyticsIcon,
  Timeline as TimelineIcon,
  ShowChart as ShowChartIcon,
  AttachMoney as AttachMoneyIcon
} from "@mui/icons-material";
const YearlyGraph = () => {
    const [monthlyGraphList, setMonthlyGraphList] = useState([]);
    const [avarage, setAvarage] = useState(0);
    const [fundStats, setFundStats] = useState({
        totalUsers: 0,
        totalDeposits: 0,
        totalWithdrawals: 0,
        netRevenue: 0,
        bestMonth: null,
        worstMonth: null,
        volatility: 0,
        avgMonthlyPerformance: 0
    });
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        getMonthlyList();
    }, []);
    const getMonthlyList = async () => {
        setIsLoading(true);
        // Get ALL yearly_graph entries to properly filter to last 12 months
        const q = query(collection(DATABASE, "yearly_graph"), orderBy('created_at', "desc"));
        const unsubscribe = onSnapshot(q, async (querySnapshot) => {
            let transactions = querySnapshot?.docs?.map((doc) => ({
                ...doc.data(),
            }));
            
            // Group by unique month/year combinations (prevent duplicates)
            const monthlyMap = new Map();
            transactions.forEach(entry => {
                const key = `${entry.month}-${entry.year}`;
                if (!monthlyMap.has(key)) {
                    monthlyMap.set(key, entry);
                } else {
                    // Replace with newer entry if this one is more recent
                    const existing = monthlyMap.get(key);
                    const existingDate = existing?.created_at?.toDate 
                        ? existing.created_at.toDate() 
                        : existing?.created_at instanceof Date 
                            ? existing.created_at 
                            : new Date(0);
                    const currentDate = entry?.created_at?.toDate 
                        ? entry.created_at.toDate() 
                        : entry?.created_at instanceof Date 
                            ? entry.created_at 
                            : new Date(0);
                    if (currentDate > existingDate) {
                        monthlyMap.set(key, entry);
                    }
                }
            });
            
            // Convert back to array and sort by year/month descending
            transactions = Array.from(monthlyMap.values()).sort((a, b) => {
                if (a.year !== b.year) return b.year - a.year;
                return b.month - a.month;
            });
            
            // Take the most recent 12 months
            transactions = transactions.slice(0, 12);
            
            // Reverse to show oldest to newest (matches Flutter logic)
            transactions = [...transactions].reverse();
            
            // Calculate 12-month total from monthly amounts (same as Flutter app)
            const total = transactions?.reduce((total, item) => {
                // Use ONLY amount field (same as Flutter app: graph.amount)
                const monthlyPercentage = item?.amount || 0;
                return total + monthlyPercentage;
            }, 0);
            
            // Calculate average
            const average = transactions.length > 0 ? total / transactions.length : 0;
            
            setAvarage(total);
            setMonthlyGraphList(transactions);
            
            // Now calculate stats with loaded monthly data
            await calculateFundStats(transactions);
            setIsLoading(false);

        }, (error) => {
            console.error("Error fetching yearly:", error);
            setIsLoading(false);
        });
        return unsubscribe;
    }
    
    const calculateFundStats = async (monthlyData) => {
        try {
            // Get total users (only active users with role === 1)
            const usersQuery = query(collection(DATABASE, "users"));
            const usersSnapshot = await getDocs(usersQuery);
            const totalUsers = usersSnapshot.docs.filter(doc => doc.data().role === 1 && doc.data().status === 1).length;

            // Get all transaction requests for comprehensive analysis
            const allTransactionsQuery = query(collection(DATABASE, "transection_requests"));
            const allTransactionsSnapshot = await getDocs(allTransactionsQuery);
            const companyFlowDocs = allTransactionsSnapshot.docs.filter(
                doc => !isCapitalRoutingRow(doc.data())
            );
            
            // Calculate deposits (request_type === 0 and status === 2)
            const totalDeposits = companyFlowDocs
                .filter(doc => doc.data().request_type === 0 && doc.data().request_status === 2)
                .reduce((sum, doc) => sum + (doc.data().total_amount || 0), 0);

            // Calculate withdrawals (request_type === 1 and status === 5)
            const totalWithdrawals = companyFlowDocs
                .filter(doc => doc.data().request_type === 1 && doc.data().request_status === 5)
                .reduce((sum, doc) => sum + (doc.data().withdraw_amount || 0), 0);

            // Calculate net revenue
            const netRevenue = totalDeposits - totalWithdrawals;

            // Calculate best and worst months
            let bestMonth = null;
            let worstMonth = null;
            let maxPerformance = -Infinity;
            let minPerformance = Infinity;

            monthlyData.forEach((month, index) => {
                // Use ONLY amount field (same as Flutter app)
                const performance = month?.amount || 0;
                
                if (!isNaN(performance) && performance > maxPerformance) {
                    maxPerformance = performance;
                    bestMonth = { month: index + 1, performance };
                }
                if (!isNaN(performance) && performance < minPerformance) {
                    minPerformance = performance;
                    worstMonth = { month: index + 1, performance };
                }
            });

            // Calculate volatility (standard deviation) - fixed to prevent NaN
            let volatility = 0;
            if (monthlyData.length > 0) {
                const validPerformances = monthlyData
                    .map(month => month?.amount || 0)
                    .filter(p => !isNaN(p));
                
                if (validPerformances.length > 0) {
                    const mean = validPerformances.reduce((sum, p) => sum + p, 0) / validPerformances.length;
                    const variance = validPerformances.reduce((sum, p) => sum + Math.pow(p - mean, 2), 0) / validPerformances.length;
                    volatility = Math.sqrt(variance);
                }
            }

            // Calculate additional metrics - now uses the monthlyData parameter
            const avgMonthlyPerformance = monthlyData.length > 0 
                ? monthlyData.reduce((sum, month) => {
                    // Use ONLY amount field (same as Flutter app)
                    const p = month?.amount || 0;
                    return sum + (isNaN(p) ? 0 : p);
                }, 0) / monthlyData.length 
                : 0;

            setFundStats({
                totalUsers,
                totalDeposits,
                totalWithdrawals,
                netRevenue,
                bestMonth,
                worstMonth,
                volatility,
                avgMonthlyPerformance
            });
        } catch (error) {
            console.error("Error calculating fund statistics:", error);
        }
    }


    const getMonthName = (monthNumber) => {
        const months = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
        return months[monthNumber - 1] || 'Unknown';
    };

    return (
        <Box sx={{ 
            width: "100%", 
            p: 3,
            background: "linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)",
            minHeight: "100vh"
        }}>
            {/* Header */}
            <Box sx={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center", 
                mb: 3,
                paddingTop: { xs: '60px', sm: "0px" }
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <AnalyticsIcon sx={{ fontSize: 40, color: "#2af598" }} />
                    <Box>
                        <Typography variant="h4" component="h1" sx={{ fontWeight: "600", color: "#fff" }}>
                            12-Month Global Performance
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#aaa" }}>
                            {monthlyGraphList.length > 0 
                              ? `Current 12-month fund performance analysis and historical data from ${getMonthName(monthlyGraphList[0].month)} ${monthlyGraphList[0].year} to ${getMonthName(monthlyGraphList[monthlyGraphList.length - 1].month)} ${monthlyGraphList[monthlyGraphList.length - 1].year}`
                              : "Current 12-month fund performance analysis and historical data"
                            }
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="Refresh Data">
                        <IconButton 
                            onClick={() => getMonthlyList()}
                            sx={{ 
                                color: "#fff",
                                backgroundColor: "rgba(42, 245, 152, 0.1)",
                                "&:hover": { backgroundColor: "rgba(42, 245, 152, 0.2)" }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Data">
                        <IconButton sx={{ 
                            color: "#fff",
                            backgroundColor: "rgba(42, 245, 152, 0.1)",
                            "&:hover": { backgroundColor: "rgba(42, 245, 152, 0.2)" }
                        }}>
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Compact Performance Summary - Single Line */}
            <Card sx={{
                background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a2a 100%)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                mb: 3
            }}>
                <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 4, flexWrap: 'wrap' }}>
                        <Box>
                            <Typography variant="body2" sx={{ color: "#aaa", mb: 0.5 }}>Total Performance</Typography>
                            <Typography variant="h5" sx={{ fontWeight: "bold", color: "#4ade80" }}>
                                {avarage.toFixed(2)}%
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" sx={{ color: "#aaa", mb: 0.5 }}>Avg Monthly</Typography>
                            <Typography variant="h5" sx={{ fontWeight: "bold", color: "#4ade80" }}>
                                {fundStats.avgMonthlyPerformance.toFixed(2)}%
                            </Typography>
                        </Box>
                        {fundStats.bestMonth && (
                            <Box>
                                <Typography variant="body2" sx={{ color: "#aaa", mb: 0.5 }}>Best Month</Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Chip 
                                        label={getMonthName(fundStats.bestMonth.month)} 
                                        sx={{ 
                                            backgroundColor: "rgba(74, 222, 128, 0.2)",
                                            color: "#4ade80",
                                            border: "1px solid rgba(74, 222, 128, 0.3)",
                                            fontWeight: 600
                                        }}
                                        size="small" 
                                    />
                                    <Typography variant="body1" sx={{ fontWeight: "600", color: "#4ade80" }}>
                                        {fundStats.bestMonth.performance.toFixed(2)}%
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        {fundStats.worstMonth && (
                            <Box>
                                <Typography variant="body2" sx={{ color: "#aaa", mb: 0.5 }}>Worst Month</Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Chip 
                                        label={getMonthName(fundStats.worstMonth.month)} 
                                        sx={{ 
                                            backgroundColor: "rgba(245, 42, 45, 0.2)",
                                            color: "#f87171",
                                            border: "1px solid rgba(245, 42, 45, 0.3)",
                                            fontWeight: 600
                                        }}
                                        size="small" 
                                    />
                                    <Typography variant="body1" sx={{ fontWeight: "600", color: "#f87171" }}>
                                        {fundStats.worstMonth.performance.toFixed(2)}%
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Performance Analysis - Graph First Priority */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12}>
                    <Card sx={{
                        background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)"
                    }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <ShowChartIcon sx={{ mr: 1, color: "#2af598" }} />
                                    <Typography variant="h5" sx={{ fontWeight: "600", color: "#fff" }}>
                                        Fund Performance - Last 12 Months
                                    </Typography>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: "600", color: "#2af598" }}>
                                    Total: {avarage.toFixed(2)}%
                                </Typography>
                            </Box>
                            {isLoading ? (
                                <Skeleton height={500} />
                            ) : (
                                <ShowYearlyGraph
                                    performanceGraphDetails={monthlyGraphList}
                                    avarage={avarage}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Monthly Performance Table */}
            <Card sx={{
                background: "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)"
            }}>
                <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: "600", mb: 2, color: "#fff" }}>
                        Monthly Performance Details
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{
                        backgroundColor: "transparent",
                        "& .MuiTable-root": {
                            backgroundColor: "transparent"
                        }
                    }}>
                        <Table>
                            <TableHead sx={{
                                background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                                fontFamily: "Outfit",
                                borderRadius: '10px',
                                textAlign: "center",
                                position: 'sticky',
                                top: 0,
                                zIndex: 1 
                            }}>
                                <TableRow>
                                    <TableCell sx={{ color: "#fff", fontWeight: "600" }}><strong>Month</strong></TableCell>
                                    <TableCell align="right" sx={{ color: "#fff", fontWeight: "600" }}><strong>Performance</strong></TableCell>
                                    <TableCell align="right" sx={{ color: "#fff", fontWeight: "600" }}><strong>Point</strong></TableCell>
                                    <TableCell align="right" sx={{ color: "#fff", fontWeight: "600" }}><strong>Status</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {monthlyGraphList.map((month, index) => {
                                    // Use ONLY amount field (same as Flutter app)
                                    const performance = month?.amount || 0;
                                    const point = month?.point || 0;
                                    const isPositive = performance >= 0;
                                    
                                    return (
                                        <TableRow key={index} sx={{
                                            "&:hover": {
                                                backgroundColor: "rgba(255, 255, 255, 0.05)"
                                            }
                                        }}>
                                            <TableCell sx={{ color: "#fff" }}>
                                                {month.month && month.year ? 
                                                    `${String(month.month).padStart(2, '0')}/${month.year.toString().slice(-2)}` : 
                                                    getMonthName(month.month || (index + 1))
                                                }
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography 
                                                    variant="body1" 
                                                    sx={{ 
                                                        fontWeight: "600",
                                                        color: isPositive ? "#4ade80" : "#f87171"
                                                    }}
                                                >
                                                    {performance.toFixed(2)}%
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        color: "#aaa",
                                                        fontFamily: "monospace"
                                                    }}
                                                >
                                                    {point.toFixed(2)}%
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Chip 
                                                    label={isPositive ? "Gain" : "Loss"} 
                                                    sx={{ 
                                                        backgroundColor: isPositive ? "rgba(74, 222, 128, 0.15)" : "rgba(248, 113, 113, 0.15)",
                                                        color: isPositive ? "#4ade80" : "#f87171",
                                                        border: isPositive ? "1px solid rgba(74, 222, 128, 0.3)" : "1px solid rgba(248, 113, 113, 0.3)",
                                                        fontWeight: 600
                                                    }}
                                                    size="small" 
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default YearlyGraph;